import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfWeek, endOfWeek, addDays, format } from 'date-fns';
import type { DaySpecificTimes } from '@/types/shift-templates';

export interface TemplateWithCaregivers {
  id: string;
  name: string;
  default_start_time: string;
  default_end_time: string;
  is_overnight: boolean;
  has_variable_hours: boolean;
  day_specific_times: DaySpecificTimes | null;
  caregivers: { user_id: string; full_name: string | null }[];
}

export interface PatientSchedule {
  patient_id: string;
  patient_nickname: string;
  patient_photo_url: string | null;
  templates: TemplateWithCaregivers[];
}

export interface RealShift {
  id: string;
  patient_id: string;
  caregiver_id: string;
  caregiver_name: string | null;
  shift_template_id: string | null;
  shift_template_name: string | null;
  check_in_at: string;
  check_out_at: string | null;
  is_swap: boolean;
  original_template_id: string | null;
  original_template_name: string | null;
}

export function useCompanySchedule(companyId: string | undefined, weekStart: Date) {
  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });

  // Fetch templates and caregiver links for all company patients
  const { data: patientSchedules = [], isLoading: isTemplatesLoading } = useQuery({
    queryKey: ['company-schedule-templates', companyId],
    queryFn: async (): Promise<PatientSchedule[]> => {
      if (!companyId) return [];

      // Get company patients
      const { data: assignments } = await supabase
        .from('company_patient_assignments')
        .select('patient_id')
        .eq('company_id', companyId)
        .eq('is_active', true);

      if (!assignments?.length) return [];
      const patientIds = assignments.map(a => a.patient_id);

      // Fetch patients
      const { data: patients } = await supabase
        .from('patients')
        .select('id, nickname, photo_url')
        .in('id', patientIds)
        .order('nickname');

      if (!patients?.length) return [];

      // Fetch templates for these patients
      const { data: templates } = await supabase
        .from('shift_templates')
        .select('*')
        .in('patient_id', patientIds)
        .is('deleted_at', null)
        .order('name');

      // Fetch caregiver links with template assignments
      const { data: links } = await supabase
        .from('patient_user_links')
        .select('user_id, patient_id, shift_template_id')
        .in('patient_id', patientIds)
        .eq('role', 'caregiver')
        .eq('active', true)
        .not('shift_template_id', 'is', null);

      // Fetch profiles for linked caregivers
      const caregiverIds = [...new Set((links || []).map(l => l.user_id))];
      let profileMap: Record<string, string | null> = {};
      if (caregiverIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, full_name')
          .in('user_id', caregiverIds);
        profileMap = Object.fromEntries((profiles || []).map(p => [p.user_id, p.full_name]));
      }

      return patients.map(patient => ({
        patient_id: patient.id,
        patient_nickname: patient.nickname,
        patient_photo_url: patient.photo_url,
        templates: (templates || [])
          .filter(t => t.patient_id === patient.id)
          .map(t => ({
            id: t.id,
            name: t.name,
            default_start_time: t.default_start_time,
            default_end_time: t.default_end_time,
            is_overnight: t.is_overnight ?? false,
            has_variable_hours: t.has_variable_hours ?? false,
            day_specific_times: t.day_specific_times as unknown as DaySpecificTimes | null,
            caregivers: (links || [])
              .filter(l => l.shift_template_id === t.id && l.patient_id === patient.id)
              .map(l => ({ user_id: l.user_id, full_name: profileMap[l.user_id] ?? null })),
          })),
      }));
    },
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000,
  });

  // Fetch real shifts for the selected week
  const { data: realShifts = [], isLoading: isShiftsLoading } = useQuery({
    queryKey: ['company-schedule-shifts', companyId, format(weekStart, 'yyyy-MM-dd')],
    queryFn: async (): Promise<RealShift[]> => {
      if (!companyId) return [];

      const { data: assignments } = await supabase
        .from('company_patient_assignments')
        .select('patient_id')
        .eq('company_id', companyId)
        .eq('is_active', true);

      if (!assignments?.length) return [];
      const patientIds = assignments.map(a => a.patient_id);

      const { data: shifts } = await supabase
        .from('shifts')
        .select('id, patient_id, caregiver_id, shift_template_id, check_in_at, check_out_at, is_swap, original_template_id')
        .in('patient_id', patientIds)
        .gte('check_in_at', weekStart.toISOString())
        .lte('check_in_at', weekEnd.toISOString())
        .order('check_in_at');

      if (!shifts?.length) return [];

      // Get caregiver profiles
      const caregiverIds = [...new Set(shifts.map(s => s.caregiver_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .in('user_id', caregiverIds);
      const profileMap = Object.fromEntries((profiles || []).map(p => [p.user_id, p.full_name]));

      // Get template names for shift_template_id and original_template_id
      const templateIds = [...new Set([
        ...shifts.map(s => s.shift_template_id).filter(Boolean),
        ...shifts.map(s => s.original_template_id).filter(Boolean),
      ])] as string[];

      let templateNameMap: Record<string, string> = {};
      if (templateIds.length > 0) {
        const { data: templates } = await supabase
          .from('shift_templates')
          .select('id, name')
          .in('id', templateIds);
        templateNameMap = Object.fromEntries((templates || []).map(t => [t.id, t.name]));
      }

      return shifts.map(s => ({
        id: s.id,
        patient_id: s.patient_id,
        caregiver_id: s.caregiver_id,
        caregiver_name: profileMap[s.caregiver_id] ?? null,
        shift_template_id: s.shift_template_id,
        shift_template_name: s.shift_template_id ? templateNameMap[s.shift_template_id] ?? null : null,
        check_in_at: s.check_in_at,
        check_out_at: s.check_out_at,
        is_swap: s.is_swap ?? false,
        original_template_id: s.original_template_id,
        original_template_name: s.original_template_id ? templateNameMap[s.original_template_id] ?? null : null,
      }));
    },
    enabled: !!companyId,
    staleTime: 60 * 1000,
  });

  return {
    patientSchedules,
    realShifts,
    isLoading: isTemplatesLoading || isShiftsLoading,
  };
}
